"use client";

import { signInWithCustomToken, signOut, setPersistence, inMemoryPersistence } from "firebase/auth";
import { createClient, Client } from "graphql-ws";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";

import env from "@/lib/env-public";
import { auth } from "@/lib/firebase/firebase-client";

import { useAuth } from "@/dashboard/contexts/auth-context";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { HttpError } from "@/dashboard/lib/api/errors";
import { retry } from "@/dashboard/lib/util/retry";

interface WSContextValue {
  client: Client | null;
  ready: boolean;
}

const WSClientContext = createContext<WSContextValue | null>(null);

/* -------------- helper -------------- */
async function mintIdToken(): Promise<string> {
  const { customToken, tenantId } = await fetchJson<{
    customToken: string;
    tenantId?: string;
  }>("/api/dashboard/id-token");

  await setPersistence(auth, inMemoryPersistence);

  /* set the tenant ID **before** exchanging the custom token */
  (auth as any).tenantId = tenantId ?? null;

  const cred = await signInWithCustomToken(auth, customToken);
  const idToken = await cred.user.getIdToken(true);

  /* clear tenant + purge creds */
  (auth as any).tenantId = null;
  await signOut(auth).catch(() => {});

  return idToken;
}

async function getFreshIdToken() {
  return retry(mintIdToken, 3, 1000);
}

/* -------------- provider -------------- */
export function WSClientProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<Client | null>(null);
  const [ready, setReady] = useState(false);
  const { signOut: triggerLogout } = useAuth();

  useEffect(() => {
    let disposed = false;

    const build = async () => {
      try {
        const ws = createClient({
          url: env.NEXT_PUBLIC_HASURA_GRAPHQL_WS_URL,
          lazy: true,
          keepAlive: 30_000,
          connectionParams: async () => ({
            headers: { Authorization: `Bearer ${await getFreshIdToken()}` },
          }),
        });

        if (disposed) {
          return;
        }

        setClient(ws);
        setReady(true);
      } catch (err) {
        if (disposed) {
          return;
        }

        if (err instanceof HttpError && err.status === 401) {
          await triggerLogout();
          return;
        }

        // App is still usable, but realtime subscriptions will not work
        console.error("[WS] realtime disabled:", err);
      }
    };

    void build();
    return () => {
      disposed = true;
    };
  }, [triggerLogout]);

  return <WSClientContext.Provider value={{ client, ready }}>{children}</WSClientContext.Provider>;
}

export function useWSClient(): WSContextValue {
  const ctx = useContext(WSClientContext);
  if (!ctx) {
    throw new Error("useWSClient must be used within a WSClientProvider");
  }
  return ctx;
}
